// Browser video recording with a parallel audio-only track.
//
// Workstream K Slice 1 Stage 4. ONE getUserMedia (video + audio) feeds TWO
// MediaRecorders off the single stream:
//   (a) a full VIDEO recorder — the canonical ~150MB story artifact;
//   (b) a parallel AUDIO-only recorder — the small IVC/STT feed object.
// No silence autostop and no countdown: termination is the manual Done button
// (opts.stopSignal) or a max-duration backstop. The audio-only interview path
// stays in audioRecorder.ts (recordWithSilenceDetection), UNTOUCHED — this file
// only reuses its pickMimeType() helper for the parallel-audio MIME.

import { pickMimeType } from "./audioRecorder";

// Prefer WebM (VP8/opus): Chrome records a blob-URL-previewable container, so the
// reviewing <video> shows picture. mp4 is LAST as the iOS-forced fallback —
// Chrome's fragmented-MP4 output plays audio + duration but no picture from a blob
// URL (the Stage-4 no-video bug). iOS (MediaRecorder is mp4-only) is verified
// on-device Jun 17, NOT here; mp4 must stay supported so iOS doesn't throw.
const VIDEO_MIME_CANDIDATES = [
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4",
];

const VIDEO_BITS_PER_SECOND = 2_500_000;
const AUDIO_BITS_PER_SECOND = 128_000;
const DEFAULT_MAX_DURATION_MS = 300_000; // 5 min backstop (~98MB at the bitrates above)

export class VideoUnsupportedError extends Error {
  constructor(reason: string) {
    super(`video recording unavailable: ${reason}`);
    this.name = "VideoUnsupportedError";
  }
}

export class CameraPermissionDeniedError extends Error {
  constructor() {
    super("camera/microphone permission denied");
    this.name = "CameraPermissionDeniedError";
  }
}

export type VideoRecordOptions = {
  maxDurationMs?: number;
  stopSignal?: AbortSignal;
};

export type VideoRecordResult = {
  videoBlob: Blob;
  audioBlob: Blob;
  videoContentType: "video/webm" | "video/mp4";
  audioContentType: string;
  durationMs: number;
  // Why the recording ended. No silence autostop on the video path, so the union
  // is only these two ("manual" = Done/abort, "max_duration" = the cap backstop).
  stoppedReason: "manual" | "max_duration";
};

const pickVideoMimeType = (): string => {
  if (typeof MediaRecorder === "undefined") return "";
  for (const candidate of VIDEO_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return "";
};

// Normalize "video/webm;codecs=vp8,opus" -> "video/webm" and assert it is one
// of the two containers the presign + finalize key build accept. Anything else
// rejects, because a mismatch here would diverge the R2 key extension.
const normalizeVideoContentType = (raw: string): "video/webm" | "video/mp4" | null => {
  const base = (raw || "").split(";")[0].trim().toLowerCase();
  if (base === "video/webm") return "video/webm";
  if (base === "video/mp4") return "video/mp4";
  return null;
};

export const recordVideoWithParallelAudio = async (
  opts: VideoRecordOptions = {},
): Promise<VideoRecordResult> => {
  const { maxDurationMs = DEFAULT_MAX_DURATION_MS, stopSignal } = opts;

  if (typeof MediaRecorder === "undefined") {
    throw new VideoUnsupportedError("MediaRecorder not supported");
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
      audio: true,
    });
  } catch (err) {
    const name = (err as Error)?.name;
    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
      throw new CameraPermissionDeniedError();
    }
    throw new VideoUnsupportedError(name ?? "getUserMedia failed");
  }

  const videoMime = pickVideoMimeType();
  if (!videoMime) {
    stream.getTracks().forEach((t) => t.stop());
    throw new VideoUnsupportedError("no supported video MIME type");
  }
  const audioMime = pickMimeType(); // reuse the audio-path picker (no duplication)

  // Recorder A: full video (audio muxed in) — the canonical story artifact.
  const videoRecorder = new MediaRecorder(stream, {
    mimeType: videoMime,
    videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
    audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
  });

  // Recorder B: parallel audio-only — the small IVC/STT feed.
  const audioStream = new MediaStream(stream.getAudioTracks());
  const audioRecorderOpts: MediaRecorderOptions = { audioBitsPerSecond: AUDIO_BITS_PER_SECOND };
  if (audioMime) audioRecorderOpts.mimeType = audioMime;
  const audioRecorder = new MediaRecorder(audioStream, audioRecorderOpts);

  const videoChunks: Blob[] = [];
  const audioChunks: Blob[] = [];
  videoRecorder.addEventListener("dataavailable", (e) => {
    if (e.data && e.data.size > 0) videoChunks.push(e.data);
  });
  audioRecorder.addEventListener("dataavailable", (e) => {
    if (e.data && e.data.size > 0) audioChunks.push(e.data);
  });

  const videoActualMime = videoRecorder.mimeType || videoMime;
  const audioActualMime = audioRecorder.mimeType || audioMime || "audio/webm";

  const startedAt = performance.now();
  let maxTimerId = 0;
  let settled = false;
  // First stop path to fire locks the reason; the second stopBoth() no-op cannot
  // overwrite it. Default "manual" so a normal Done (or abort) yields "manual".
  let stoppedReason: "manual" | "max_duration" = "manual";
  let reasonLocked = false;
  const setReasonOnce = (r: "manual" | "max_duration") => {
    if (!reasonLocked) { reasonLocked = true; stoppedReason = r; }
  };

  const stopBoth = () => {
    if (videoRecorder.state !== "inactive") {
      try { videoRecorder.stop(); } catch { /* already stopped */ }
    }
    if (audioRecorder.state !== "inactive") {
      try { audioRecorder.stop(); } catch { /* already stopped */ }
    }
  };

  const cleanupTracks = () => {
    clearTimeout(maxTimerId);
    stream.getTracks().forEach((t) => t.stop());
  };

  // Both recorders must emit "stop" before we assemble the blobs. Count to 0.
  const completion = new Promise<VideoRecordResult>((resolve, reject) => {
    let remaining = 2;
    const onOneStopped = () => {
      if (settled) return;
      remaining -= 1;
      if (remaining > 0) return;
      settled = true;
      cleanupTracks();
      const durationMs = performance.now() - startedAt;
      const videoBlob = new Blob(videoChunks, { type: videoActualMime });
      const audioBlob = new Blob(audioChunks, { type: audioActualMime });
      const normalized = normalizeVideoContentType(videoActualMime || videoBlob.type);
      if (!normalized) {
        reject(new VideoUnsupportedError(`unexpected video container: ${videoActualMime}`));
        return;
      }
      resolve({
        videoBlob,
        audioBlob,
        videoContentType: normalized,
        audioContentType: audioBlob.type || audioActualMime,
        durationMs,
        stoppedReason,
      });
    };
    const onError = (e: Event) => {
      if (settled) return;
      settled = true;
      cleanupTracks();
      reject(new VideoUnsupportedError(`recorder error: ${e.type}`));
    };
    videoRecorder.addEventListener("stop", onOneStopped);
    audioRecorder.addEventListener("stop", onOneStopped);
    videoRecorder.addEventListener("error", onError);
    audioRecorder.addEventListener("error", onError);
  });

  maxTimerId = window.setTimeout(() => { setReasonOnce("max_duration"); stopBoth(); }, maxDurationMs);

  // Manual terminator: Done aborts stopSignal -> stop both -> single finalize.
  if (stopSignal && !stopSignal.aborted) {
    stopSignal.addEventListener("abort", () => { setReasonOnce("manual"); stopBoth(); }, { once: true });
  }

  videoRecorder.start();
  audioRecorder.start();

  // If the gesture aborted before start wired up, stop immediately post-start.
  if (stopSignal?.aborted) { setReasonOnce("manual"); stopBoth(); }

  return completion;
};
